import { Card, CardContent, CardHeader, CardTitle, LoadingSpinner, TextLink } from '@shared/components';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import { DetailList } from './DetailList';
import type { Dealer } from '@/features/dealers/types/dealers.types';

interface Props {
    dealerId: string;
}

const headingId = 'agreement-dealer-heading';

const formatAddress = (dealer: Dealer): string | undefined => {
    const parts = [dealer.address_line_1, dealer.address_line_2, dealer.city, dealer.postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : undefined;
};

/** The agreement stores `dealer_id` only — there is no dealer snapshot, so these
 *  are the dealer's CURRENT details rather than those held at the point of sale. */
export function AgreementDealerPanel({ dealerId }: Props) {
    const query = useDealerQuery(dealerId, { suppressForbiddenToast: true });

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <span id={headingId}>Selling dealer</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId}>
                    <DealerBody dealerId={dealerId} dealer={query.data} isLoading={query.isLoading} />
                </section>
            </CardContent>
        </Card>
    );
}

interface BodyProps {
    dealerId: string;
    dealer: Dealer | undefined;
    isLoading: boolean;
}

function DealerBody({ dealerId, dealer, isLoading }: BodyProps) {
    if (!dealerId) {
        return <DetailList fields={[]} emptyMessage="No selling dealer was recorded on this agreement." />;
    }

    if (isLoading) {
        return <LoadingSpinner size="sm" message="Loading dealer details" />;
    }

    if (!dealer) {
        return (
            <p className="text-sm text-mp-muted">
                Dealer details could not be loaded.{' '}
                <span className="font-mono text-xs" title={dealerId}>
                    {dealerId}
                </span>
            </p>
        );
    }

    return (
        <DetailList
            emptyMessage="No selling dealer details are recorded against this dealer."
            fields={[
                {
                    label: 'Name',
                    value: dealer.name ? (
                        <TextLink to={`/admin/dealers/${dealerId}`} color="primary">
                            {dealer.name}
                        </TextLink>
                    ) : undefined,
                },
                { label: 'Address', value: formatAddress(dealer) },
                { label: 'Phone', value: dealer.phone || undefined },
                { label: 'Email', value: dealer.email || undefined },
            ]}
        />
    );
}
